/**
 * Sun times for the SUNSET tag and Today's daylight line (Almanac decisions 4
 * and 5).
 *
 * The sun is read at the place the user actually is: inside a trip window the
 * destination, otherwise home. Pure functions only — the provider
 * (`contexts/ThemeContext.tsx`) owns the clock, the fetches and the tick.
 *
 * Day and night are geographic: they are read from the coordinates and the
 * instant alone (`sunTimes`), never from a calendar day, a clock or a zone,
 * device or transported, so the runner's zone cannot change them. A window's
 * `effective_timezone` travels for display (S3), not for this decision.
 * Mirrored at `mobile/src/lib/sun.ts`; both are bound by
 * `docs/plans/almanac-sun-vectors.json`.
 *
 * Coordinates handled here stay in memory: nothing in this module logs, stores
 * or reports them.
 */
import SunCalc from 'suncalc';

/** One element of `next-trip`'s `surface_windows` (server: planner/next_trip.py). */
export interface SurfaceWindow {
  id: number;
  destination_lat: number | null;
  destination_lng: number | null;
  effective_timezone: string;
  clock_source: 'destination' | 'origin' | 'preference' | 'utc';
  first_stop_at: string | null;
  last_stop_at: string | null;
}

export interface HomePoint {
  lat: number;
  lng: number;
}

/** One solar cycle's sunrise and sunset. */
export interface SunTimes {
  sunrise: Date;
  sunset: Date;
}

/**
 * The sunrise and sunset of the solar cycle `SunCalc.getTimes` selects when
 * handed `now` itself: the cycle at (lat, lng) whose solar noon, worked out
 * from longitude alone, is nearest `now` (so within 12 hours of it). `null`
 * when that cycle has no sunrise or no sunset (polar day or night) or an input
 * is unreadable; never a throw.
 *
 * The anchor is `now` and nothing derived from it. No calendar day and no zone
 * enter, because a zone need not describe the coordinates it travels with (a
 * home read on a device in another country, a window resolved on its origin or
 * preference zone). An anchor built from a zone's day or wall clock picks a
 * neighbouring cycle whenever it does not (a Tokyo home on a New York device at
 * 16:00 in Tokyo), and whenever the zone's offset sits about a day from its
 * longitude (Pacific/Kiritimati, UTC+14 at 157 degrees west). The nearest
 * cycle is defined for any coordinates at any instant.
 */
export function sunTimes(now: Date, lat: number, lng: number): SunTimes | null {
  try {
    // An unreadable instant or coordinate reaches suncalc as NaN and comes
    // back as an Invalid Date, like a polar cycle; a non-Date throws.
    const { sunrise, sunset } = SunCalc.getTimes(now, lat, lng);
    if (Number.isNaN(sunrise.getTime()) || Number.isNaN(sunset.getTime())) return null;
    return { sunrise, sunset };
  } catch {
    return null;
  }
}

/**
 * The first window (server tie order) that has coordinates and stop instants
 * and contains `now`, inclusive at both ends.
 */
export function containingWindow(
  windows: readonly SurfaceWindow[],
  now: Date,
): SurfaceWindow | null {
  const t = now.getTime();
  for (const w of windows) {
    if (w.destination_lat == null || w.destination_lng == null) continue;
    if (!w.first_stop_at || !w.last_stop_at) continue;
    const first = Date.parse(w.first_stop_at);
    const last = Date.parse(w.last_stop_at);
    if (Number.isNaN(first) || Number.isNaN(last)) continue;
    if (first <= t && t <= last) return w;
  }
  return null;
}
